import { createContext, useState, useContext, useEffect, useMemo } from "react";
import {
  MdHub,
  MdGroups,
  MdBarChart,
  MdTimeline,
  MdHistory,
  MdCable,
  MdStorage,
  MdDashboard,
  MdMonitor,
} from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import DiscoverIcon from "../../assets/discover.svg";
import EngageIcon from "../../assets/engage.svg";
import MeasureIcon from "../../assets/measure.svg";
import { AuthContext } from "../../contexts/auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { useLocation } from "react-router-dom";
import config from "../../config";
const SidebarContext = createContext();

export function SidebarProvider({ children }) {
  const { getFednDetails, appMetadata, getorganizationById } =
    useContext(AuthContext);
  const [isSubSidebarCollapsed, setIsCollapsed] = useState(false);
  const [activeSubItem, setActiveSubItem] = useState(null);
  const [fedSpecificMenuItems, setFedSpecificMenuItems] = useState([]);
  const [isSideBarMenuDisabled, setIsSideBarMenuDisabled] = useState({
    fed: false,
    ianda: false,
    cp: false,
    pr: false,
    ca: false,
    ah: false,
    dm: false,
  });
  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );
  console.log({ storedPermissions });
  const location = useLocation();

  const federationkey = localStorage.getItem("federationkey");
  const noFedSelected =
    !federationkey || federationkey === "null" || federationkey === "";

  const outOfFedScope =
    noFedSelected ||
    location.pathname.includes("/federation-selection") ||
    location.pathname.includes("/choose-federation");

  const tenantObj = appMetadata ? Object.values(appMetadata) : [];
  const userOrganization = tenantObj[0]?.organization;
  const authRole = tenantObj[0]?.roles?.[0];
  const isPlatformOrAppAdmin =
    authRole === config.superUserRole ||
    authRole === config.applicationAdminRole;
  const isSuperUserReadOnly =
    localStorage.getItem("isSuperUserReadOnly") === "true";

  const getorganizationByIdFn = useAsyncFn({
    deferFn: getorganizationById,
  });
  const { run: runGetOrganizationById } = getorganizationByIdFn;

  useEffect(() => {
    if (!isPlatformOrAppAdmin && userOrganization) {
      runGetOrganizationById({ organizationID: userOrganization });
    }
  }, [isPlatformOrAppAdmin, userOrganization, runGetOrganizationById]);

  const isDatasourceAvailable =
    isPlatformOrAppAdmin ||
    !getorganizationByIdFn.data ||
    getorganizationByIdFn.data?.datasource_present === "available" ||
    getorganizationByIdFn.data?.datasource_present === true;

  const getFednDetailsFn = useAsyncFn({
    deferFn: getFednDetails,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      federationkey != null &&
      federationkey !== "null" &&
      federationkey !== ""
    ) {
      getFednDetailsFn.run({ federationID: federationkey });
    }
  }, [federationkey]); // eslint-disable-line react-hooks/exhaustive-deps
  const fedData = useMemo(
    () => getFednDetailsFn?.data,
    [getFednDetailsFn?.data],
  );

  // API historically used "camapaign_history"; normalize to the correct key.
  const normalizeMenuValue = (value) =>
    value === "camapaign_history" ? "campaign_history" : value;

  useEffect(() => {
    try {
      const menuValues = fedData?.menu_items?.map((item) =>
        normalizeMenuValue(item.value),
      );
      setFedSpecificMenuItems(menuValues);
      localStorage.setItem("fedSpecificMenuItems", menuValues);
    } catch (err) {
      console.error("Error parsing fedSpecificMenuItems:", err);
    }
  }, [fedData]);

  const hasAccess = (key) => {
    if (key === "createfederation") return true;
    if (key === "systemhealth") return authRole === config.superUserRole;
    if (key === "managementdashboard") return isPlatformOrAppAdmin;
    if (key === "organizationdashboard")
      return (
        isPlatformOrAppAdmin ||
        authRole === "organization_admin" ||
        (storedPermissions["organization_dashboard"] &&
          storedPermissions["organization_dashboard"] !== "no")
      );
    const access = storedPermissions[key];
    return access && access !== "no";
  };

  const getLabel = (value, defaultLabel) => {
    const match = fedData?.menu_items?.find(
      (item) => normalizeMenuValue(item.value) === value,
    );

    if (match?.label) {
      const stored = JSON.parse(
        localStorage.getItem("fedSpecificMenuLabels") || "{}",
      );
      stored[value] = match.label;
      localStorage.setItem("fedSpecificMenuLabels", JSON.stringify(stored));
      return match.label;
    }

    const cachedLabels = JSON.parse(
      localStorage.getItem("fedSpecificMenuLabels") || "{}",
    );
    if (value === "campaign_history") {
      return (
        cachedLabels.campaign_history ||
        cachedLabels.camapaign_history ||
        defaultLabel
      );
    }
    return cachedLabels[value] || defaultLabel;
  };

  const allCurationItems = [
        {
          id: "fed",
          icon: MdHub,
          userRoleKey: "createfederation",
          label: getLabel("federation", "Federation"),
          value: "federation",
          urls: [
            ...(storedPermissions.createfederation !== "no"
              ? ["/federation-selection"]
              : []),
            "/choose-federation",
            ...(storedPermissions.createfederation !== "no"
              ? ["/organization-selection"]
              : []),
          ],
        },
        {
          id: "cp",
          icon: MdGroups,
          userRoleKey: "campaignplanning",
          label: getLabel("campaign_planning", "Audience Curation"),
          urls: ["/campaign-planning"],
          value: "campaign_planning",
        },
        {
          id: "cr",
          icon: MdBarChart,
          label: getLabel("campaign_reports", "Audience Details"),
          userRoleKey: "query",
          urls: ["/campaign-reports"],
          value: "campaign_reports",
        },
        {
          id: "pr",
          icon: MdTimeline,
          label: getLabel("provenance_trail", "Provenance Record"),
          userRoleKey: "provenancetrail",
          urls: ["/provenance-record"],
          value: "provenance_trail",
        },
        {
          id: "ah",
          icon: MdHistory,
          userRoleKey: "activationhistory",
          label: getLabel("campaign_history", "Audience History"),
          urls: ["/activation-history"],
          value: "campaign_history",
        },
        {
          id: "ca",
          icon: MdCable,
          label: getLabel("activation_connectors", "Activation Channels"),
          userRoleKey: "activationconnectors",
          urls: ["/activation-channels"],
          value: "activation_connectors",
        },
        {
          id: "dm",
          icon: MdStorage,
          userRoleKey: "datamanager",
          label: "Dataset Manager",
          urls: ["/dataset-manager"],
          value: "datamanager",
        },
        {
          id: "md",
          icon: MdDashboard,
          userRoleKey: "managementdashboard",
          label: "Management Dashboard",
          urls: ["/management-dashboard"],
          value: "management_dashboard",
        },
        {
          id: "od",
          icon: MdDashboard,
          userRoleKey: "organizationdashboard",
          label: "Organization Dashboard",
          urls: ["/organization-dashboard"],
          value: "organization_dashboard",
        },
        {
          id: "sh",
          icon: MdMonitor,
          userRoleKey: "systemhealth",
          label: "System Health",
          urls: ["/system-health"],
          value: "system_health",
        },
      ];

  const mainMenuItems = [
    {
      icon: DiscoverIcon,
      name: "Curation",
      subMenu: allCurationItems
        .filter((item) => {
          if (isSuperUserReadOnly) {
            return (
              item.value === "federation" ||
              (!outOfFedScope && item.value === "provenance_trail") ||
              item.value === "management_dashboard" ||
              item.value === "organization_dashboard" ||
              item.value === "system_health"
            );
          }
          if (outOfFedScope) {
            // only show Federation + Activation Channels + Management Dashboard + Organization Dashboard on the Federation selection page
            return (
              item.value === "federation" ||
              item.value === "activation_connectors" ||
              item.value === "management_dashboard" ||
              item.value === "organization_dashboard" ||
              item.value === "system_health"
            );
          }
          // normal behavior
          return (
            item.value === "federation" ||
            item.value === "activation_connectors" ||
            item.value === "management_dashboard" || // TODO: remove when admin app is configured
            item.value === "organization_dashboard" ||
            item.value === "system_health" ||
            fedSpecificMenuItems?.includes(item.value)
          );
        })
        .filter((item) => hasAccess(item.userRoleKey))
        .filter((item) => {
          if (item.value === "datamanager" && !isDatasourceAvailable) {
            return false;
          }
          return true;
        }),
    },
    {
      icon: MeasureIcon,
      name: "Insights (Coming Soon)",
      subMenu: [],
    },
    {
      icon: EngageIcon,
      name: "ID Resolution (Coming Soon)",
      subMenu: [],
    },
  ];

  /* For the ONAM side panel (FEATURES.curationSkin, onam/chrome/CurationSide.tsx): the pages that belong to a
     federation, and the ones that work across federations. A federation's pages are listed even while none is
     open (shown unavailable), so the panel doesn't change shape when you go back to choose a federation.
     Which pages a federation has comes from its menu, the same as above. */
  const FED_SCOPED = ["campaign_planning", "campaign_reports", "provenance_trail", "campaign_history", "datamanager"];
  const fedOpen = !outOfFedScope;
  const curationNav = {
    fedOpen,
    federation: fedOpen ? mainMenuItems[0].subMenu.filter((item) => FED_SCOPED.includes(item.value)) : allCurationItems
      .filter((item) => FED_SCOPED.includes(item.value))
      .filter((item) => (isSuperUserReadOnly ? item.value === "provenance_trail" : true))
      .filter((item) => !fedSpecificMenuItems?.length || fedSpecificMenuItems.includes(item.value))
      .filter((item) => hasAccess(item.userRoleKey))
      .filter((item) => item.value !== "datamanager" || isDatasourceAvailable),
    global: mainMenuItems[0].subMenu.filter((item) => !FED_SCOPED.includes(item.value)),
  };

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);

  return (
    <SidebarContext.Provider
      value={{
        isSubSidebarCollapsed,
        toggleSidebar,
        activeSubItem,
        setActiveSubItem,
        mainMenuItems,
        isSideBarMenuDisabled,
        setIsSideBarMenuDisabled,
        curationNav,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  return useContext(SidebarContext);
}
